import { Checkbox, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import clsx from "clsx";
import { useEffect, useState, type FC } from "react";
import { CustomArrow } from "./CustomArrow";
import { type Skill, type Team } from "@entities";
import "./OneTeam.scss";
import "./TeamsSelect.scss";
import { CheckboxIcon } from "@shared/frontend/icons";

interface OneTeamProps {
  team: Team | Skill | { id: string, name: string, category: string; };
  chosen?: boolean;
  checked?: boolean;
  onChange?: (teamId: string, checked: boolean) => void;
  hasChildren?: boolean;
  onForwardClick?: () => void;
  onBackClick?: () => void;
  onNameClick?: (row: Team | Skill | { id: string, name: string, category: string; }) => void;
  hideCheckbox?: boolean;
  className?: string;
  tooltip?: boolean;
}

export const OneTeamChip: FC<OneTeamProps> = (props) => {
  const {
    team,
    hasChildren,
    chosen,
    checked,
    onForwardClick,
    onChange,
    onNameClick,
    onBackClick,
    hideCheckbox = false,
    tooltip = true,
    className = "",
  } = props;
  const [timeoutState, setTimeoutState] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (tooltipOpen) {
      const timeout = setTimeout(() => {
        setTooltipOpen(false);
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [tooltipOpen]);

  function handleTooltipHover() {
    if (!tooltipOpen) {
      setTimeoutState(
        setTimeout(() => {
          setTooltipOpen(true);
        }, 300),
      );
    }
  }

  function handleTooltipClose() {
    setTooltipOpen(false);
    if (timeoutState) clearTimeout(timeoutState);
  }

  return (
    <MenuItem
      value={team.name}
      className={`team-menu-item ${className}`}
      disableGutters
      disableRipple
      disableTouchRipple
    >
      {!hideCheckbox && (
        <Checkbox
          sx={{ padding: "5px 0" }}
          disableTouchRipple
          checkedIcon={<CheckboxIcon checked />}
          icon={<CheckboxIcon />}
          onChange={(_, checked) => onChange && onChange(team.id, checked)}
          checked={checked}
        />
      )}
      {chosen && <CustomArrow arrowDirection="back" onClick={onBackClick} />}
      <Stack
        className={clsx("one-team-container", hideCheckbox && "no-hover")}
        direction="row"
      >
        <Tooltip
          onMouseLeave={handleTooltipClose}
          open={tooltip && tooltipOpen}
          arrow
          title={team.name}
        >
          <Typography
            onClick={() => onNameClick && onNameClick(team)}
            onMouseLeave={handleTooltipClose}
            onMouseOver={handleTooltipHover}
            className="team-name"
          >
            {team.name}
          </Typography>
        </Tooltip>
      </Stack>
      {hasChildren && (
        <CustomArrow arrowDirection="forward" onClick={onForwardClick} className="team-arrow" />
      )}
    </MenuItem>
  );
};
